/**
 * Per-profile progress (local-first, synced with the server via mergeProgress).
 *
 * Sense of progress: sparks → levels 1..7 → bonus features + stickers.
 * Path: sequential lessons, a path key per completed level opens the next level, intro coin pieces.
 * FTUE: playground first ("draw two to unlock" Learn) → intro path coin → unlock-all chest → done.
 */
import {
  BONUS_FEATURES,
  COIN_PIECES,
  FTUE_DRAWINGS_TO_UNLOCK_LEARN,
  LEVEL_THRESHOLDS,
  SKILL_LEVEL_THRESHOLDS,
  SPARKS_BY_CONTENT_TYPE,
  STICKERS_PER_LEVEL,
  type BonusFeatureId,
  type SparkContentType,
} from './constants';
import type { PathId, SkillId, SubjectCategory } from './content/model';
import { emptyMailing, mergeMailing, type MailingState } from './mailing';
import { emptyCollection, legacyCollections, mergeCollection, normalizeCollection, type CollectionState } from './collect/state';

export type CoinPiece = (typeof COIN_PIECES)[number];
export type FtueStage = 'playground' | 'learn' | 'chest' | 'done';

export interface WeeklyEntry {
  itemId: string;
  creditedAt: string;
}

export interface ProgressState {
  v: 1;
  totalSparks: number;
  skillXp: Partial<Record<SkillId, number>>;
  completedMiniUnits: string[];
  /** Resume point: index of the last completed item per mini unit. */
  miniUnitSteps: Record<string, number>;
  completedPlaygroundItemIds: string[];
  favorites: string[];
  coinPieces: CoinPiece[];
  /** Highest path level whose key has been earned, per path. */
  pathKeys: Partial<Record<PathId, number>>;
  completedPaths: PathId[];
  currentPath: PathId;
  ftue: { stage: FtueStage; drawings: number; choice: 'path' | 'playground' | null };
  drawingsCount: number;
  drawTogetherCount: number;
  presentedBonusFeatures: string[];
  /** Last level whose level-up celebration was shown. */
  lastSeenLevel: number;
  /** Sticker count the user has already been told about. */
  seenStickerCount: number;
  /** Earned skill badges, `${skill}:${level}`. */
  badges: string[];
  activeDays: string[];
  /** Gallery-selection answers, keyed by item id. */
  choices: Record<string, string>;
  weekly: Record<string, { entries: WeeklyEntry[]; rewardClaimed: boolean }>;
  weeklyIntroSeen: boolean;
  mailing: MailingState;
  stories: { unlockedAt: string | null; seen: string[] };
  studio: { wall: string[]; completedFlows: string[] };
  /** Seen walkthroughs / first-time overlays. */
  seen: string[];
  /** Skill journey step captures (journal item ids), per skill. */
  skillJourneys: Partial<Record<SkillId, JourneyProgress>>;
  /** Collections + habit (album stickers, opened cards, gallery frames, weekly goal); see collect/state.ts. */
  collect: CollectionState;
  /**
   * Monetization: the child's wish jar (locked things tapped → "add to my wishes"); parents see it in
   * the grown-ups corner and the weekly report. Never shows a price to the child.
   */
  wishes: WishEntry[];
  /**
   * Curriculum v2 migration (docs/CURRICULUM-V2.md §F): mini-unit ids of NEW activities inserted into
   * levels the child had already reached, per path. They show "New!", count for the world but never block.
   * Frozen once (deterministic) by `migrateProgressToCurriculumV2`.
   */
  grandfathered: Partial<Record<PathId, string[]>>;
  /** Set once the v2 migration ran for this profile (CONTENT_VERSION 3). */
  curriculumV2At: string | null;
  /** v2 level challenges: earned "Confident artist" stars per path (levels 1..4). */
  confidentStars: Partial<Record<PathId, number[]>>;
  /** v2 resumable lessons (masterpiece): chapter index reached per mini unit. */
  chapterResume: Record<string, number>;
  updatedAt: string;
}

export type WishKind = 'node' | 'playground' | 'world' | 'story' | 'skin' | 'feature';
export interface WishEntry {
  kind: WishKind;
  /** Content id (mini unit, playground item, path id, skin name…). */
  id: string;
  /** Profile-local label key is derived from the id by the client; no free text. */
  at: string;
  /** A parent has seen it in the grown-ups corner. */
  seenByParent?: boolean;
}

export interface JourneyCapture {
  stepId: string;
  /** Journal item holding the photo (client id). */
  journalId: string;
  at: string;
}

export interface JourneyProgress {
  captures: JourneyCapture[];
  completedAt: string | null;
}

export function emptyProgress(): ProgressState {
  return {
    v: 1,
    totalSparks: 0,
    skillXp: {},
    completedMiniUnits: [],
    miniUnitSteps: {},
    completedPlaygroundItemIds: [],
    favorites: [],
    coinPieces: [],
    pathKeys: {},
    completedPaths: [],
    currentPath: 'intro',
    ftue: { stage: 'playground', drawings: 0, choice: null },
    drawingsCount: 0,
    drawTogetherCount: 0,
    presentedBonusFeatures: [],
    lastSeenLevel: 1,
    seenStickerCount: STICKERS_PER_LEVEL[1] ?? 0,
    badges: [],
    activeDays: [],
    choices: {},
    weekly: {},
    weeklyIntroSeen: false,
    mailing: emptyMailing(),
    stories: { unlockedAt: null, seen: [] },
    studio: { wall: [], completedFlows: [] },
    seen: [],
    skillJourneys: {},
    collect: emptyCollection(),
    wishes: [],
    grandfathered: {},
    curriculumV2At: null,
    confidentStars: {},
    chapterResume: {},
    updatedAt: new Date(0).toISOString(),
  };
}

/** Fill in fields missing from older / partial snapshots (forward-compatible sync). */
export function normalizeProgress(p: Partial<ProgressState> | null | undefined): ProgressState {
  const e = emptyProgress();
  if (!p || typeof p !== 'object') return e;
  return {
    ...e,
    ...p,
    v: 1,
    ftue: { ...e.ftue, ...(p.ftue ?? {}) },
    mailing: { ...e.mailing, ...(p.mailing ?? {}) },
    stories: { ...e.stories, ...(p.stories ?? {}) },
    studio: { ...e.studio, ...(p.studio ?? {}) },
    skillJourneys: p.skillJourneys && typeof p.skillJourneys === 'object' ? p.skillJourneys : {},
    collect: normalizeCollection(p.collect),
    wishes: Array.isArray(p.wishes) ? p.wishes.filter((w) => !!w && typeof w.id === 'string' && typeof w.kind === 'string') : [],
    grandfathered: p.grandfathered && typeof p.grandfathered === 'object' ? p.grandfathered : {},
    curriculumV2At: typeof p.curriculumV2At === 'string' ? p.curriculumV2At : null,
    confidentStars: p.confidentStars && typeof p.confidentStars === 'object' ? p.confidentStars : {},
    chapterResume: p.chapterResume && typeof p.chapterResume === 'object' ? p.chapterResume : {},
  };
}

/* ------------------------------------ Wishes ------------------------------------ */

/** Max new wishes shown per day to parents (MONETIZATION-PLAN §6); the jar itself keeps them all (deduped). */
export const WISHES_MAX = 50;

/** Add a wish (deduped by kind + id; newest last). */
export function addWish(prev: ProgressState, kind: WishKind, id: string, now = new Date()): ProgressState {
  if (prev.wishes.some((w) => w.kind === kind && w.id === id)) return prev;
  const wishes = [...prev.wishes, { kind, id, at: now.toISOString() }].slice(-WISHES_MAX);
  return { ...prev, wishes, updatedAt: now.toISOString() };
}

/** A parent opened the wish list. */
export function markWishesSeen(prev: ProgressState, now = new Date()): ProgressState {
  if (prev.wishes.every((w) => w.seenByParent)) return prev;
  return { ...prev, wishes: prev.wishes.map((w) => ({ ...w, seenByParent: true })), updatedAt: now.toISOString() };
}

/** Drop wishes that became available (e.g. after purchase) — the client passes a predicate. */
export function pruneWishes(prev: ProgressState, stillLocked: (w: WishEntry) => boolean, now = new Date()): ProgressState {
  const wishes = prev.wishes.filter(stillLocked);
  return wishes.length === prev.wishes.length ? prev : { ...prev, wishes, updatedAt: now.toISOString() };
}

export function dayKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/* ------------------------------- Levels / sparks ------------------------------- */

export interface LevelInfo {
  level: number;
  /** Sparks still needed for the next level (0 at max level). */
  toNext: number;
  /** 0..1 progress inside the current level (1 at max). */
  fraction: number;
  isMax: boolean;
}

/** level = max{i : total ≥ thresholds[i]} (1-based). */
export function levelFor(totalSparks: number): LevelInfo {
  let idx = 0;
  for (let i = 0; i < LEVEL_THRESHOLDS.length; i++) if (totalSparks >= LEVEL_THRESHOLDS[i]!) idx = i;
  const isMax = idx === LEVEL_THRESHOLDS.length - 1;
  const cur = LEVEL_THRESHOLDS[idx]!;
  const next = isMax ? cur : LEVEL_THRESHOLDS[idx + 1]!;
  return {
    level: idx + 1,
    toNext: isMax ? 0 : next - totalSparks,
    fraction: isMax ? 1 : (totalSparks - cur) / (next - cur),
    isMax,
  };
}

export const unlockedFeatures = (totalSparks: number): BonusFeatureId[] =>
  BONUS_FEATURES.filter((f) => totalSparks >= f.sparks).map((f) => f.id);

export const isFeatureUnlocked = (p: ProgressState, f: BonusFeatureId, unlockAll = false) =>
  unlockAll || p.totalSparks >= (BONUS_FEATURES.find((b) => b.id === f)?.sparks ?? 0);

export const featureRequirement = (f: BonusFeatureId) => BONUS_FEATURES.find((b) => b.id === f)?.sparks ?? 0;

/** Number of stickers available at a level (cumulative). */
export function stickersAvailable(level: number): number {
  let n = 0;
  for (let l = 1; l <= level; l++) n += STICKERS_PER_LEVEL[l] ?? 0;
  return n;
}

/* ----------------------------------- Skills ----------------------------------- */

export function skillLevel(xp: number): { level: number; toNext: number; fraction: number } {
  let idx = 0;
  for (let i = 0; i < SKILL_LEVEL_THRESHOLDS.length; i++) if (xp >= SKILL_LEVEL_THRESHOLDS[i]!) idx = i;
  const isMax = idx === SKILL_LEVEL_THRESHOLDS.length - 1;
  const cur = SKILL_LEVEL_THRESHOLDS[idx]!;
  const next = isMax ? cur : SKILL_LEVEL_THRESHOLDS[idx + 1]!;
  return { level: idx + 1, toNext: isMax ? 0 : next - xp, fraction: isMax ? 1 : (xp - cur) / (next - cur) };
}

/* ----------------------------------- Awards ----------------------------------- */

export interface AwardInput {
  kind: SparkContentType;
  now: Date;
  /** Path lesson / playground item that was completed. */
  miniUnitId?: string;
  playgroundItemId?: string;
  skillXp?: Partial<Record<SkillId, number>>;
  /** Counts as a finished drawing (photo captured or skipped at the end of a drawing lesson). */
  isDrawing?: boolean;
  coinPiece?: CoinPiece;
  /** Set when this completion finished a path level (unit) → path key. */
  pathKey?: { path: PathId; level: number };
  /** Set when this completion finished the whole path. */
  pathCompleted?: PathId;
  /** Drawing metadata used for weekly challenge credit. */
  drawing?: { itemId: string; category: SubjectCategory; subject: string };
  /** Active weekly challenge (if any) and whether the drawing qualifies. */
  challenge?: { id: string; qualifies: boolean; target: number };
  /** Envelope size for teacher letters. */
  envelopeSize?: number;
  /** v2: sparks override (per activity type, SPARKS_BY_ACTIVITY). */
  sparks?: number;
  /** v2 level challenge passed with a photo and within the allowed peeks → one "Confident artist" star. */
  confidentStar?: { path: PathId; level: number };
}

export interface SparksReceipt {
  sparks: number;
  sparksBefore: number;
  sparksAfter: number;
  levelBefore: number;
  levelAfter: number;
  newFeatures: BonusFeatureId[];
  newStickers: number;
  skillXp: Partial<Record<SkillId, number>>;
  skillLevelUps: { skill: SkillId; level: number }[];
  coinPiece: CoinPiece | null;
  coinComplete: boolean;
  pathKey: { path: PathId; level: number } | null;
  pathCompleted: PathId | null;
  ftueStageBefore: FtueStage;
  ftueStageAfter: FtueStage;
  challengeCredited: string | null;
  challengeCompleted: string | null;
  envelopeFull: boolean;
}

const uniqPush = <T>(arr: T[], v: T) => (arr.includes(v) ? arr : [...arr, v]);

/** Pure: apply one completion and return the new state plus a receipt for the celebration chain. */
export function applyAward(prev: ProgressState, input: AwardInput): { state: ProgressState; receipt: SparksReceipt } {
  const s: ProgressState = structuredClone(prev);
  const sparks = input.sparks ?? SPARKS_BY_CONTENT_TYPE[input.kind];
  const before = levelFor(s.totalSparks);
  const featuresBefore = unlockedFeatures(s.totalSparks);
  const ftueBefore = s.ftue.stage;
  s.totalSparks += sparks;
  const after = levelFor(s.totalSparks);

  // Completion sets.
  if (input.miniUnitId) {
    s.completedMiniUnits = uniqPush(s.completedMiniUnits, input.miniUnitId);
    delete s.miniUnitSteps[input.miniUnitId];
    delete s.chapterResume[input.miniUnitId];
  }
  if (input.confidentStar) {
    const { path, level } = input.confidentStar;
    s.confidentStars[path] = uniqPush(s.confidentStars[path] ?? [], level).sort();
  }
  if (input.playgroundItemId) s.completedPlaygroundItemIds = uniqPush(s.completedPlaygroundItemIds, input.playgroundItemId);

  // Skill XP + badges.
  const skillLevelUps: SparksReceipt['skillLevelUps'] = [];
  for (const [k, xp] of Object.entries(input.skillXp ?? {}) as [SkillId, number][]) {
    const was = skillLevel(s.skillXp[k] ?? 0).level;
    s.skillXp[k] = (s.skillXp[k] ?? 0) + xp;
    const now = skillLevel(s.skillXp[k]!).level;
    if (now > was) {
      skillLevelUps.push({ skill: k, level: now });
      s.badges = uniqPush(s.badges, `${k}:${now}`);
    }
  }

  // Drawings + FTUE.
  if (input.isDrawing) {
    s.drawingsCount += 1;
    if (s.ftue.stage === 'playground') {
      s.ftue.drawings += 1;
      if (s.ftue.drawings >= FTUE_DRAWINGS_TO_UNLOCK_LEARN) s.ftue.stage = 'learn';
    }
  }

  // Coin pieces.
  let coinPiece: CoinPiece | null = null;
  if (input.coinPiece && !s.coinPieces.includes(input.coinPiece)) {
    coinPiece = input.coinPiece;
    s.coinPieces = [...s.coinPieces, input.coinPiece];
  }
  const coinComplete = coinPiece !== null && COIN_PIECES.every((c) => s.coinPieces.includes(c));
  if (coinComplete && (s.ftue.stage === 'learn' || s.ftue.stage === 'playground')) s.ftue.stage = 'chest';

  // Path keys.
  let pathKey: SparksReceipt['pathKey'] = null;
  if (input.pathKey && (s.pathKeys[input.pathKey.path] ?? 0) < input.pathKey.level) {
    s.pathKeys[input.pathKey.path] = input.pathKey.level;
    pathKey = input.pathKey;
  }
  let pathCompleted: PathId | null = null;
  if (input.pathCompleted && !s.completedPaths.includes(input.pathCompleted)) {
    s.completedPaths = [...s.completedPaths, input.pathCompleted];
    pathCompleted = input.pathCompleted;
  }

  // Weekly challenge credit.
  let challengeCredited: string | null = null;
  let challengeCompleted: string | null = null;
  if (input.challenge?.qualifies && input.drawing) {
    const w = (s.weekly[input.challenge.id] ??= { entries: [], rewardClaimed: false });
    if (w.entries.length < input.challenge.target) {
      w.entries.push({ itemId: input.drawing.itemId, creditedAt: input.now.toISOString() });
      challengeCredited = input.challenge.id;
      if (w.entries.length >= input.challenge.target) challengeCompleted = input.challenge.id;
    }
  }

  // Teacher-letter envelope energy.
  let envelopeFull = false;
  if (input.isDrawing) {
    const size = input.envelopeSize ?? 5;
    if (!s.mailing.pending && s.mailing.energy < size) {
      s.mailing.energy += 1;
      envelopeFull = s.mailing.energy >= size;
    }
  }

  s.activeDays = uniqPush(s.activeDays, dayKey(input.now));
  s.updatedAt = input.now.toISOString();
  const featuresAfter = unlockedFeatures(s.totalSparks);
  const newFeatures = featuresAfter.filter((f) => !featuresBefore.includes(f));
  if (newFeatures.includes('stories') && !s.stories.unlockedAt) s.stories.unlockedAt = input.now.toISOString();

  return {
    state: s,
    receipt: {
      sparks,
      sparksBefore: prev.totalSparks,
      sparksAfter: s.totalSparks,
      levelBefore: before.level,
      levelAfter: after.level,
      newFeatures,
      newStickers: stickersAvailable(after.level) - stickersAvailable(before.level),
      skillXp: input.skillXp ?? {},
      skillLevelUps,
      coinPiece,
      coinComplete,
      pathKey,
      pathCompleted,
      ftueStageBefore: ftueBefore,
      ftueStageAfter: s.ftue.stage,
      challengeCredited,
      challengeCompleted,
      envelopeFull,
    },
  };
}

/** Record resume position inside a mini unit. */
export function saveMiniUnitStep(prev: ProgressState, miniUnitId: string, index: number, now = new Date()): ProgressState {
  if ((prev.miniUnitSteps[miniUnitId] ?? -1) >= index) return prev;
  return { ...prev, miniUnitSteps: { ...prev.miniUnitSteps, [miniUnitId]: index }, updatedAt: now.toISOString() };
}

/** Finish the FTUE chest: unlock every path (subject to premium) and remember the choice. */
export function finishUnlockAll(prev: ProgressState, choice: 'path' | 'playground', path?: PathId, now = new Date()): ProgressState {
  return {
    ...prev,
    ftue: { ...prev.ftue, stage: 'done', choice },
    currentPath: path ?? prev.currentPath,
    updatedAt: now.toISOString(),
  };
}

export function markSeen(prev: ProgressState, key: string, now = new Date()): ProgressState {
  if (prev.seen.includes(key)) return prev;
  return { ...prev, seen: [...prev.seen, key], updatedAt: now.toISOString() };
}

/* ------------------------------------ Sync ------------------------------------ */

const union = <T>(a: T[], b: T[]) => [...new Set([...a, ...b])];
const FTUE_ORDER: FtueStage[] = ['playground', 'learn', 'chest', 'done'];

/**
 * Merge two progress snapshots (device + server). Monotonic counters take the max, sets are
 * unioned, and per-key choices/preferences come from the newer snapshot. Never loses progress.
 */
export function mergeProgress(aIn: ProgressState, bIn: ProgressState): ProgressState {
  const a = normalizeProgress(aIn);
  const b = normalizeProgress(bIn);
  const newer = a.updatedAt >= b.updatedAt ? a : b;
  const older = newer === a ? b : a;
  const skillXp: Partial<Record<SkillId, number>> = {};
  for (const k of new Set([...Object.keys(a.skillXp), ...Object.keys(b.skillXp)]) as Set<SkillId>) {
    skillXp[k] = Math.max(a.skillXp[k] ?? 0, b.skillXp[k] ?? 0);
  }
  const pathKeys: Partial<Record<PathId, number>> = {};
  for (const k of new Set([...Object.keys(a.pathKeys), ...Object.keys(b.pathKeys)]) as Set<PathId>) {
    pathKeys[k] = Math.max(a.pathKeys[k] ?? 0, b.pathKeys[k] ?? 0);
  }
  const miniUnitSteps: Record<string, number> = {};
  const completed = union(a.completedMiniUnits, b.completedMiniUnits);
  for (const k of new Set([...Object.keys(a.miniUnitSteps), ...Object.keys(b.miniUnitSteps)])) {
    if (completed.includes(k)) continue;
    miniUnitSteps[k] = Math.max(a.miniUnitSteps[k] ?? -1, b.miniUnitSteps[k] ?? -1);
  }
  const weekly: ProgressState['weekly'] = {};
  for (const k of new Set([...Object.keys(a.weekly), ...Object.keys(b.weekly)])) {
    const x = a.weekly[k];
    const y = b.weekly[k];
    const entries = [...(x?.entries ?? [])];
    for (const e of y?.entries ?? []) if (!entries.some((z) => z.itemId === e.itemId && z.creditedAt === e.creditedAt)) entries.push(e);
    entries.sort((p, q) => p.creditedAt.localeCompare(q.creditedAt));
    weekly[k] = { entries, rewardClaimed: !!(x?.rewardClaimed || y?.rewardClaimed) };
  }
  const stage = FTUE_ORDER[Math.max(FTUE_ORDER.indexOf(a.ftue.stage), FTUE_ORDER.indexOf(b.ftue.stage))]!;
  return {
    v: 1,
    totalSparks: Math.max(a.totalSparks, b.totalSparks),
    skillXp,
    completedMiniUnits: completed,
    miniUnitSteps,
    completedPlaygroundItemIds: union(a.completedPlaygroundItemIds, b.completedPlaygroundItemIds),
    favorites: newer.favorites,
    coinPieces: union(a.coinPieces, b.coinPieces),
    pathKeys,
    completedPaths: union(a.completedPaths, b.completedPaths),
    currentPath: newer.currentPath,
    ftue: { stage, drawings: Math.max(a.ftue.drawings, b.ftue.drawings), choice: newer.ftue.choice ?? older.ftue.choice },
    drawingsCount: Math.max(a.drawingsCount, b.drawingsCount),
    drawTogetherCount: Math.max(a.drawTogetherCount, b.drawTogetherCount),
    presentedBonusFeatures: union(a.presentedBonusFeatures, b.presentedBonusFeatures),
    lastSeenLevel: Math.max(a.lastSeenLevel, b.lastSeenLevel),
    seenStickerCount: Math.max(a.seenStickerCount, b.seenStickerCount),
    badges: union(a.badges, b.badges),
    activeDays: union(a.activeDays, b.activeDays).sort(),
    choices: { ...older.choices, ...newer.choices },
    weekly,
    weeklyIntroSeen: a.weeklyIntroSeen || b.weeklyIntroSeen,
    mailing: mergeMailing(a.mailing, b.mailing),
    stories: {
      unlockedAt: [a.stories.unlockedAt, b.stories.unlockedAt].filter((x): x is string => !!x).sort()[0] ?? null,
      seen: union(a.stories.seen, b.stories.seen),
    },
    studio: { wall: newer.studio.wall, completedFlows: union(a.studio.completedFlows, b.studio.completedFlows) },
    seen: union(a.seen, b.seen),
    skillJourneys: mergeJourneys(a.skillJourneys, b.skillJourneys),
    collect: mergeCollection(a.collect, b.collect),
    wishes: mergeWishes(a.wishes, b.wishes),
    grandfathered: mergeListMap(a.grandfathered, b.grandfathered),
    curriculumV2At: [a.curriculumV2At, b.curriculumV2At].filter((x): x is string => !!x).sort()[0] ?? null,
    confidentStars: mergeListMap(a.confidentStars, b.confidentStars),
    chapterResume: mergeMax(a.chapterResume, b.chapterResume, completed),
    updatedAt: newer.updatedAt,
  };
}

/**
 * One-time fold of the legacy local-only collect store (`sketchling-collect-v1`, raw JSON or parsed)
 * into each profile's progress, merging (never overwriting). Profiles missing from `progress` are
 * skipped. Returns the same object when there is nothing to fold.
 */
export function foldLegacyCollect(progress: Record<string, ProgressState>, legacy: unknown): Record<string, ProgressState> {
  const byProfile = legacyCollections(legacy);
  let out = progress;
  for (const [pid, col] of Object.entries(byProfile)) {
    const p = progress[pid];
    if (!p) continue;
    if (out === progress) out = { ...progress };
    out[pid] = { ...p, collect: mergeCollection(p.collect, col) };
  }
  return out;
}

function mergeWishes(a: WishEntry[], b: WishEntry[]): WishEntry[] {
  const out: WishEntry[] = [];
  for (const w of [...a, ...b].sort((x, y) => x.at.localeCompare(y.at))) {
    const i = out.findIndex((z) => z.kind === w.kind && z.id === w.id);
    if (i < 0) out.push({ ...w });
    else if (w.seenByParent) out[i] = { ...out[i]!, seenByParent: true };
  }
  return out.slice(-WISHES_MAX);
}

function mergeListMap<K extends string, V>(a: Partial<Record<K, V[]>>, b: Partial<Record<K, V[]>>): Partial<Record<K, V[]>> {
  const out: Partial<Record<K, V[]>> = {};
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)]) as Set<K>) out[k] = union(a[k] ?? [], b[k] ?? []);
  return out;
}

function mergeMax(a: Record<string, number>, b: Record<string, number>, completed: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) if (!completed.includes(k)) out[k] = Math.max(a[k] ?? 0, b[k] ?? 0);
  return out;
}

/** Union of journey captures (by step + journal id); a journey completed on either side stays completed. */
function mergeJourneys(a: ProgressState['skillJourneys'], b: ProgressState['skillJourneys']): ProgressState['skillJourneys'] {
  const out: ProgressState['skillJourneys'] = {};
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)]) as Set<SkillId>) {
    const x = a[k];
    const y = b[k];
    const ok = (c: unknown): c is JourneyCapture => !!c && typeof c === 'object' && typeof (c as JourneyCapture).stepId === 'string' && typeof (c as JourneyCapture).journalId === 'string' && typeof (c as JourneyCapture).at === 'string';
    const list = (j: JourneyProgress | undefined) => (Array.isArray(j?.captures) ? j.captures.filter(ok) : []);
    const captures = [...list(x)];
    for (const c of list(y)) if (!captures.some((z) => z.stepId === c.stepId && z.journalId === c.journalId)) captures.push(c);
    captures.sort((p, q) => p.at.localeCompare(q.at));
    const done = [x?.completedAt, y?.completedAt].filter((v): v is string => typeof v === 'string' && !!v).sort()[0] ?? null;
    out[k] = { captures, completedAt: done };
  }
  return out;
}
